<mat-slide-toggle
  class="m-b-16 d-block"
  [checked]="isSurveyEnabled"
  (change)="onSurveyEnabledChange($event)"
  >
  Enable Exit Survey
</mat-slide-toggle>

<div class="m-b-16">
  <h4>Survey Title</h4>
  <mat-form-field class="w-full" appearance="outline">
    <input matInput [(ngModel)]="surveyTitle" placeholder="Enter survey title here..." />
    <mat-hint>The title displayed at the top of the survey dialog.</mat-hint>
  </mat-form-field>
  <button mat-raised-button class="m-t-8" color="accent" (click)="saveTitle()">Save Title</button>
</div>

<div class="m-b-16">
  <h4>Survey Description</h4>
  <mat-form-field class="w-full" appearance="outline">
    <textarea
      matInput
      [(ngModel)]="surveyDescription"
      placeholder="Enter survey description here..."
      rows="3"
    ></textarea>
    <mat-hint
      >Optional description displayed below the title. Leave empty to show no description.</mat-hint
      >
    </mat-form-field>
    <button mat-raised-button class="m-t-8" color="accent" (click)="saveDescription()">
      Save Description
    </button>
  </div>

  <div class="m-b-16">
    <h4>Survey Disclaimer</h4>
    <mat-form-field class="w-full" appearance="outline">
      <textarea
        matInput
        [(ngModel)]="surveyDisclaimer"
        placeholder="Enter survey disclaimer here..."
        rows="2"
      ></textarea>
      <mat-hint
        >Optional disclaimer displayed as small text under the buttons. Leave empty to show no
        disclaimer.</mat-hint
        >
      </mat-form-field>
      <button mat-raised-button class="m-t-8" color="accent" (click)="saveDisclaimer()">
        Save Disclaimer
      </button>
    </div>

    <div class="m-b-16">
      <h4>Button Texts</h4>
      <div class="button-texts-section">
        <div class="button-text-field">
          <h5>Submit Button Text</h5>
          <mat-form-field class="w-full" appearance="outline">
            <input matInput [(ngModel)]="submitButtonText" placeholder="Submit Survey" />
            <mat-hint>Text for the submit button. Default: "Submit Survey"</mat-hint>
          </mat-form-field>
          <button mat-raised-button class="m-t-8" color="accent" (click)="saveSubmitButtonText()">
            Save Submit Text
          </button>
        </div>

        <div class="button-text-field m-t-16">
          <h5>Skip Button Text</h5>
          <mat-form-field class="w-full" appearance="outline">
            <input matInput [(ngModel)]="skipButtonText" placeholder="Skip Survey" />
            <mat-hint>Text for the skip button. Default: "Skip Survey"</mat-hint>
          </mat-form-field>
          <button mat-raised-button class="m-t-8" color="accent" (click)="saveSkipButtonText()">
            Save Skip Text
          </button>
        </div>
      </div>
    </div>

    <div class="m-b-16">
      <h4>Survey Questions</h4>
      <p class="text-muted">
        Add questions that will be rated on a scale of 1-10. Survey appears when users are about to
        leave the site.
      </p>

      <!-- Add new question -->
      <div class="add-question-section">
        <mat-form-field class="w-full" appearance="outline">
          <input
            matInput
            [(ngModel)]="newQuestionText"
            placeholder="Enter new question..."
            (keyup.enter)="addQuestion()"
            />
        </mat-form-field>
        <button
          mat-raised-button
          color="accent"
          (click)="addQuestion()"
          [disabled]="!newQuestionText.trim()"
          class="m-t-8"
          >
          Add Question
        </button>
      </div>

      <!-- Questions list -->
      @if (questions.length > 0) {
        <div class="questions-list">
          @for (question of questions; track question) {
            <div class="question-item">
              <div class="question-text">{{ question.question }}</div>
              <button
                mat-icon-button
                color="warn"
                (click)="deleteQuestion(question.id)"
                matTooltip="Delete question"
                >
                <mat-icon>delete</mat-icon>
              </button>
            </div>
          }
        </div>
      }

      @if (questions.length === 0) {
        <div class="no-questions">
          <p class="text-muted">No questions added yet. Add your first question above.</p>
        </div>
      }
    </div>

    <!-- Export and Results Section -->
    @if (isSurveyEnabled && questions.length > 0) {
      <div class="m-b-16">
        <h4>Survey Data Management</h4>
        <div class="actions-section">
          <button mat-raised-button color="primary" (click)="exportSurveyData()" class="m-r-8">
            <mat-icon>download</mat-icon>
            Export Survey Data (CSV)
          </button>
          <button
            mat-raised-button
            color="accent"
            (click)="toggleResults()"
            [disabled]="surveyResultsLoading"
            >
            <mat-icon>{{ showResults ? 'visibility_off' : 'visibility' }}</mat-icon>
            {{ showResults ? 'Hide Results' : 'Show Results' }}
          </button>
        </div>
        <!-- Session Status and Reset -->
        <div class="session-status m-t-16">
          <div class="status-info">
            <span class="status-label">Survey status this session:</span>
            <span
              class="status-value"
              [class.shown]="hasBeenShownThisSession"
              [class.not-shown]="!hasBeenShownThisSession"
              >
              {{ hasBeenShownThisSession ? 'Already shown' : 'Not shown yet' }}
            </span>
          </div>
          <button
            mat-stroked-button
            color="warn"
            (click)="resetSurveySession()"
            [disabled]="!hasBeenShownThisSession"
            matTooltip="Reset session status to allow survey to appear again in this session"
            >
            <mat-icon>refresh</mat-icon>
            Reset Session Status
          </button>
        </div>
      </div>
    }

    <!-- Survey Results Display -->
    @if (showResults) {
      <div class="survey-results">
        <h4>Survey Results</h4>
        <!-- Loading indicator -->
        @if (surveyResultsLoading) {
          <div class="loading-section">
            <mat-spinner diameter="40"></mat-spinner>
            <p>Loading survey results...</p>
          </div>
        }
        <!-- Results content -->
        @if (!surveyResultsLoading && surveyResults.length > 0) {
          <div>
            <!-- Summary stats -->
            <div class="results-summary m-b-16">
              <p><strong>Total Responses:</strong> {{ totalResults }}</p>
              <p><strong>Page:</strong> {{ currentPage }} of {{ totalPages }}</p>
            </div>
            <!-- Average ratings per question -->
            @if (questions.length > 0) {
              <div class="average-ratings m-b-16">
                <h5>Average Ratings</h5>
                @for (question of questions; track question) {
                  <div class="rating-item">
                    <div class="question-text">{{ question.question }}</div>
                    <div class="rating-score">
                      Average: <strong>{{ getAverageRating(question.id) }}/10</strong>
                    </div>
                  </div>
                }
              </div>
            }
            <!-- Individual responses -->
            <div class="individual-responses">
              <h5>Individual Responses</h5>
              @for (result of surveyResults; track result) {
                <div class="response-item">
                  <div class="response-header">
                    <strong>{{ result.user_login }}</strong>
                    <span class="response-date">{{ result.completedAt | date: 'medium' }}</span>
                  </div>
                  <div class="response-answers">
                    @for (answer of result.answers; track answer) {
                      <div class="answer-item">
                        <span class="question-id">Q{{ +answer.questionId + 1 }}:</span>
                        <span class="rating">{{ answer.rating }}/10</span>
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
            <!-- Pagination -->
            @if (totalPages > 1) {
              <div class="pagination-controls">
                <button
                  mat-icon-button
                  [disabled]="currentPage <= 1"
                  (click)="previousPage()"
                  matTooltip="Previous page"
                  >
                  <mat-icon>chevron_left</mat-icon>
                </button>
                <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
                <button
                  mat-icon-button
                  [disabled]="currentPage >= totalPages"
                  (click)="nextPage()"
                  matTooltip="Next page"
                  >
                  <mat-icon>chevron_right</mat-icon>
                </button>
              </div>
            }
          </div>
        }
        <!-- No results message -->
        @if (!surveyResultsLoading && surveyResults.length === 0) {
          <div class="no-results">
            <p class="text-muted">No survey responses found.</p>
          </div>
        }
      </div>
    }
